import { Button, Group, LoadingOverlay, Stack, Text, Title } from "@mantine/core";
import { useCallback, useMemo, useState, type FC } from "react";
import { useForm } from "@mantine/form";
import type { BillingAddress, TaxInfo } from "@project/common";
import { PRODUCT } from "@project/common";
import { PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import type { ConfirmSetupData, StripeError, StripePaymentElementOptions } from "@stripe/stripe-js";
import { AddressForm } from "./AddressForm";
import { ErrorMessage } from "./ErrorMessage";
import { FormattedMessage, useIntl } from "react-intl";
import { billingAddressSchemaCustom } from "../utils/schema";
import { useTRPC } from "../trpc";
import { get } from "es-toolkit/compat";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { useMapkaNotification } from "../hooks/notifications";
import type { Account } from "../routes/admin/Accounts";

interface AccountLike {
  billingAddress?: BillingAddress | null;
  billingEmail: string;
  name: string;
}

function createConfirmParams(account: AccountLike): ConfirmSetupData {
  return {
    return_url: window.location.href,
    payment_method_data: {
      billing_details: {
        email: account.billingEmail,
        name: account.name,
        address: account.billingAddress?.address,
      },
    },
  };
}

const paymentElementOptions: StripePaymentElementOptions = {
  business: {
    name: PRODUCT,
  },
  wallets: {
    googlePay: "never" as const,
    link: "never" as const,
  },
};

export interface PaymentMethodFormProps {
  account: Account;
  onChangePayment?: () => void;
  onCancel?: () => void;
  cancelLabel?: string;
  saveLabel?: string;
}

export const PaymentMethodForm: FC<PaymentMethodFormProps> = ({ onChangePayment, onCancel, account, cancelLabel, saveLabel }) => {
  const intl = useIntl();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const elements = useElements();
  const stripe = useStripe();
  const mapkaNotification = useMapkaNotification();

  const [submitError, setSubmitError] = useState<Error | StripeError | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validate = useMemo(() => zod4Resolver(billingAddressSchemaCustom(intl)), [intl]);
  const { onSubmit, getInputProps, values } = useForm({
    validateInputOnBlur: true,
    initialValues: {
      type: get(account, "billingAddress.type", "personal"),
    },
    validate: validate,
  });

  const { mutateAsync: changeBillingAddress } = useMutation(trpc.changeAccountBillingAddress.mutationOptions());
  const { mutateAsync: createSetupIntent } = useMutation(trpc.createSetupIntent.mutationOptions());
  const { mutateAsync: createdPaymentMethod } = useMutation(trpc.createdPaymentMethod.mutationOptions());

  const handleSubmitPaymentMethod = useCallback(async (form: typeof values) => {
    const address = elements?.getElement("address");
    const taxId = elements?.getElement("taxId");

    if (!stripe || !elements || !address) {
      logger.error({ form }, "Failed to submit payment method.");
      return;
    }

    const { error: elementsError } = await elements.submit();
    const { value } = await address.getValue();

    let maybeTax: TaxInfo | undefined = {};
    if (form.type === "business") {
      const taxValue = await taxId?.getValue();
      if (taxValue?.value.taxId) {
        maybeTax = taxValue.value;
      }
    }

    if (elementsError) {
      logger.error({ error: elementsError }, "Failed to submit payment method.");
      setSubmitError(elementsError);
      return;
    }

    // One flag for the whole flow — the mutations are interleaved with a Stripe
    // round-trip, so per-mutation pending states make the button flicker.
    setSubmitError(null);
    setIsSubmitting(true);

    try {
      const [clientSecret, updatedAccount] = await Promise.all([
        createSetupIntent(),
        changeBillingAddress({
          ...form,
          ...value,
          ...maybeTax,
        }),
      ]);
      queryClient.setQueryData(trpc.getCurrentAccount.queryKey(), updatedAccount);

      const { error: confirmError, setupIntent } = await stripe.confirmSetup({
        elements,
        clientSecret,
        confirmParams: createConfirmParams(updatedAccount),
        redirect: "if_required",
      });

      if (confirmError) {
        logger.error({ error: confirmError }, "Failed to confirm setup intent.");
        setSubmitError(confirmError);
        return;
      }

      await createdPaymentMethod({
        id: setupIntent.payment_method as string,
      });

      await Promise.all([
        queryClient.invalidateQueries(trpc.getCurrentUser.queryFilter()),
        queryClient.invalidateQueries(trpc.getCurrentSubscriptions.queryFilter()),
        queryClient.invalidateQueries(trpc.getCurrentPaymentMethod.queryFilter()),
      ]);

      mapkaNotification({
        title: intl.formatMessage({ defaultMessage: "Saved", id: "notification.saved" }),
        message: intl.formatMessage({ defaultMessage: "Your payment method has been saved.", id: "notification.paymentMethodSaved" }),
        variant: "success",
      });
      onChangePayment?.();
    }
    catch (error) {
      logger.error({ error }, "Failed to save payment method.");
      if (error instanceof Error) {
        setSubmitError(error);
      }
    }
    finally {
      setIsSubmitting(false);
    }
  }, [
    changeBillingAddress,
    createSetupIntent,
    createdPaymentMethod,
    onChangePayment,
    stripe,
    elements,
    queryClient,
    trpc,
    intl,
    mapkaNotification,
  ]);

  if (!stripe || !elements) {
    return <LoadingOverlay />;
  }

  const isCompany = values.type === "business";

  const defaultCancelLabel = intl.formatMessage({ defaultMessage: "Cancel", id: "button.cancel" });
  const defaultSaveLabel = intl.formatMessage({ defaultMessage: "Save", id: "button.save" });
  return (
    <form onSubmit={onSubmit(handleSubmitPaymentMethod)}>
      <Stack gap="xs">
        <ErrorMessage error={submitError} />
        <Text size="sm" pb="xs">
          <FormattedMessage id="text.paymentsAreSecure" defaultMessage="All transactions are secure and encrypted." />
        </Text>
        <PaymentElement
          aria-label="Payment method"
          options={paymentElementOptions}
        />
      </Stack>

      <Stack gap="xs" pt="xs">
        <Title size="h3">
          <FormattedMessage id="title.billingAddress" defaultMessage="Billing address" />
        </Title>
        <Text size="sm" pb="xs">
          <FormattedMessage id="text.billingAddressDescription" defaultMessage="Your billing address is used to calculate taxes and to generate invoices." />
        </Text>
        <AddressForm
          getInputProps={getInputProps}
          defaultAddress={account.billingAddress}
          isPending={isSubmitting}
          isCompany={isCompany}
        />
      </Stack>
      <Group gap="xs" justify="flex-end" pt="lg">
        {onCancel && (
          <Button
            variant="secondary"
            type="reset"
            loading={isSubmitting}
            onClick={onCancel}
          >
            {cancelLabel ?? defaultCancelLabel}
          </Button>
        )}
        <Button
          variant="tertiary"
          type="submit"
          loading={isSubmitting}
        >
          {saveLabel ?? defaultSaveLabel}
        </Button>
      </Group>
    </form>
  );
};
